/* eslint-disable camelcase */
// node modules
import React, { Component, Fragment, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import PropTypes from 'prop-types';
import { connect, Provider, ReactReduxContext } from 'react-redux';
import { stringify } from 'qs';
import withStyles from 'isomorphic-style-loader/withStyles';
import StyleContext from 'isomorphic-style-loader/StyleContext';
import Skeleton from 'react-loading-skeleton';
import classNames from 'classnames';

// app modules: actions
import { loadSearchView } from 'actions/search';
import { openTableOfContents, closeTableOfContents } from 'actions/tableOfContents';

// app modules: components
import BookRankOverlay from 'components/BookRankOverlay';
import Button from 'components/Button';
import { UnorderedList, ListItem } from 'components/List';
import Icon from 'components/Icon';
import Image from 'components/Image';
import Link from 'components/Link';

// app modules: services
import formatter from 'services/formatter';
import assets from 'services/assets';
import route from 'services/route';
import history from 'services/history';

// app modules: props
import LayoutProps from 'components/Layout/LayoutProps';

// assets
import CssAppearsIn from './AppearsIn.scss';
import { BREAKPOINTS, COLOURS } from '../../constants';

const TableOfContents = React.lazy(() =>
  import(/* webpackChunkName: 'table-of-contents' */ 'components/TableOfContents'),
);
export class AppearsInComponent extends Component {
  static propTypes = {
    book: PropTypes.shape({
      authors: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string,
          first_name: PropTypes.string,
          last_name: PropTypes.string,
          avatar_name: PropTypes.string,
        }),
      ),
      image_name: PropTypes.string,
      isbn: PropTypes.string,
      publication_date: PropTypes.string,
      ranking_recommendations: PropTypes.number,
      signature_: PropTypes.string,
      title: PropTypes.string,
      title_short: PropTypes.string,
      indexable: PropTypes.bool,
      licensed: PropTypes.bool,
    }).isRequired,
    tableOfContents: PropTypes.shape({
      isOpen: PropTypes.bool,
    }).isRequired,
    openTableOfContents: PropTypes.func.isRequired,
    closeTableOfContents: PropTypes.func.isRequired,
    loadSearchView: PropTypes.func.isRequired,
    dimensions: LayoutProps.dimensions.isRequired,
    loading: PropTypes.bool,
    id: PropTypes.string,
  };

  static defaultProps = {
    loading: false,
    id: '',
  };

  constructor(props) {
    super(props);

    this.bound = {
      toggleTableOfContents: this.toggleTableOfContents.bind(this),
    };
  }

  /**
   * Doing this action here and nowhere else because the task is requiring us upon redirect to the
   * last visited page within a book to open the TOC automatically.
   *
   * This component is the only common component with a TOC across all possible book's component.
   *
   * To avoid redundancy and having to redeclare the TOC component anywhere else, it seemed only
   * logical to do the auto-toggle logic here.
   * */
  componentDidMount() {
    const { id, tableOfContents } = this.props;

    // We are relying on comparing the IDs to open the TOC because, this app isn't rendering
    // one Recipe component or on Section component at a time, there's literally multiple recipes
    // rendered each and every single time you're viewing a recipe, a reference or a section. So we
    // need to watch out what we trigger on every life cycle.
    if (
      history.location.state &&
      history.location.state.id === id &&
      this.tocStore &&
      this.tocInsertCSS &&
      !tableOfContents.isOpen
    ) {
      this.toggleTableOfContents({ store: this.tocStore, insertCss: this.tocInsertCSS });
    }
  }

  toggleTableOfContents({ store, insertCss }) {
    const { tableOfContents, book } = this.props;
    if (!book.isbn) {
      return;
    }

    if (tableOfContents.isOpen) {
      this.props.closeTableOfContents();
    } else {
      const root = createRoot(document.getElementById('modal'));
      root.render(
        <StyleContext.Provider value={{ insertCss }}>
          <Provider store={store}>
            <Suspense fallback={null}>
              <TableOfContents />
            </Suspense>
          </Provider>
        </StyleContext.Provider>,
      );

      this.props.openTableOfContents();
    }
  }

  handleRecipesButtonClick = () => {
    const { title } = this.props.book;
    const queryParams = stringify(
      { q: 'recipes', 'book_full_title[0]': title },
      { encodeValuesOnly: true },
    );
    this.props.loadSearchView();
    setTimeout(
      () => history.push(`/search?${queryParams}`, { referrer: 'Recipe', section: 'Search' }),
      0,
    );
  };

  renderRecipesButton() {
    const { indexable, licensed } = this.props.book;

    if (indexable && licensed) {
      return (
        <ListItem>
          <Button
            onClick={this.handleRecipesButtonClick}
            themeType="clear"
            themeName="energized"
            size="large"
            title="Recipes in this book"
          >
            <Icon fill={COLOURS.ENERGIZED} name="grid" height="14" width="14" mr={5} />
            Recipes
          </Button>
        </ListItem>
      );
    }

    return null;
  }

  render() {
    const { book, dimensions, loading } = this.props;

    const actionListFullWidth = dimensions.body <= BREAKPOINTS.PHONE_VERTICAL;
    const actionList = (
      <UnorderedList
        inline
        nowrap
        spaceBetween="none"
        className={actionListFullWidth ? 'appearsIn__actions-full-width' : null}
      >
        <ListItem>
          <Link
            to={book.isbn ? route.book(book.isbn, book.signature_) : '#'}
            referrer={history.location.pathname}
            className={classNames(
              'button',
              'button--size-large',
              'button--clear',
              'button--energized',
              { 'button--disabled': !book.isbn },
            )}
            title="About this book"
            disabled={!book.isbn}
          >
            <Icon fill={COLOURS.ENERGIZED} name="info-outline" height="18" width="18" mr={5} />
            About
          </Link>
        </ListItem>
        {this.renderRecipesButton()}
        <ListItem>
          <StyleContext.Consumer>
            {({ insertCss }) => (
              <ReactReduxContext.Consumer>
                {({ store }) => {
                  this.tocInsertCSS = insertCss;
                  this.tocStore = store;
                  return (
                    <Button
                      className={classNames('list__no-print-button', {
                        'button--disabled': !book.isbn,
                      })}
                      onClick={() => this.bound.toggleTableOfContents({ store, insertCss })}
                      themeType="clear"
                      themeName="energized"
                      size="large"
                      title="Show table of contents"
                    >
                      <Icon
                        className="appearsIn__contents-icon"
                        fill={COLOURS.ENERGIZED}
                        name="list"
                        height="20"
                        width="20"
                        mr={5}
                      />
                      Contents
                    </Button>
                  );
                }}
              </ReactReduxContext.Consumer>
            )}
          </StyleContext.Consumer>
        </ListItem>
      </UnorderedList>
    );

    return (
      <Fragment>
        <div className="heading margin-bottom-10">Appears in</div>
        <div className="appearsIn">
          <div className="margin-right-20">
            {loading && !book.isbn ? (
              <Skeleton width="80px" height="108px" />
            ) : (
              <Link
                to={route.book(book.isbn, book.signature_)}
                referrer={history.location.pathname}
                title="About this book"
              >
                <Image
                  overlay={<BookRankOverlay ranking={book.ranking_recommendations} />}
                  image={assets.coverUrl(book.image_name, {
                    resize: 'fit-in',
                    width: 200,
                    height: 200,
                  })}
                  height={108}
                  alt={book.title}
                />
              </Link>
            )}
          </div>

          <div className="appearsIn__details">
            <header className="margin-bottom-5">
              <div className="heading">
                {loading && !book.title_short ? (
                  <Skeleton width="150px" />
                ) : (
                  <Link
                    to={route.book(book.isbn, book.signature_)}
                    referrer={history.location.pathname}
                    title="About this book"
                  >
                    {book.title_short}
                  </Link>
                )}
              </div>

              <p className="appearsIn__paragraph">
                {loading && !book.authors.length ? (
                  <Skeleton width="150px" />
                ) : (
                  <Fragment>
                    <em className="appearsIn__emphasized">By </em>
                    {formatter.authorsList.toDOMWithLinks(book.authors)}
                  </Fragment>
                )}
              </p>
              <p className="appearsIn__paragraph">
                {loading && !book.publication_date ? (
                  <Skeleton width="150px" />
                ) : (
                  <Fragment>
                    <em className="appearsIn__emphasized">Published </em>
                    {book.publication_date}
                  </Fragment>
                )}
              </p>
            </header>
            {actionListFullWidth ? null : actionList}
          </div>
        </div>
        {actionListFullWidth ? actionList : null}
      </Fragment>
    );
  }
}

const mapStateToProps = state => ({
  tableOfContents: state.tableOfContents,
  dimensions: state.dimensions,
});

const mapDispatchToProps = {
  openTableOfContents,
  closeTableOfContents,
  loadSearchView,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withStyles(CssAppearsIn)(AppearsInComponent));
